<script setup lang="ts">
import { FormKitLazyProvider } from '@formkit/vue'
import type { Ref } from 'vue'
import ProgressSpinner from 'primevue/progressspinner'
import type { ClosingShiftForm } from '~/stores/shared/shift'
import type { Shift } from '~/types/db/shifts'
import { resolveCashWarning, resolveCountState, salesByMethod, typedCashDifference } from '~/utils/shiftCash'

interface DialogData {
  businessId?: string
  shift?: Shift
}

interface DialogRef {
  close: () => void
  data?: DialogData
}

const { t } = useNuxtApp().$i18n
const dialogRef = inject<Ref<DialogRef>>('dialogRef')

// Loading state
const loading = ref(true)
const initError = ref<string | null>(null)

// Access data through dialogRef.value.data (PrimeVue pattern)
const dialogBusinessId = computed(() => dialogRef?.value?.data?.businessId)
const dialogShift = computed(() => dialogRef?.value?.data?.shift)
const routeBusinessId = useRouteParam('buuid') as string

const businessId = dialogBusinessId.value || routeBusinessId
const shiftData = dialogShift.value

// Store and refs (will be initialized in onMounted)
let shiftStore: ReturnType<typeof useShiftStore>

// Reactive refs from store
const shift = ref<Shift | undefined>()
const closingCashAmount = ref<string | undefined>()
const permissions = ref<Record<string, boolean>>({})

// Initialize store on mount
onMounted(async () => {
  try {
    // Validate required data
    if (!businessId) {
      throw new Error('Business ID is required to close shift dialog')
    }

    if (!shiftData?.id) {
      throw new Error('Shift data is required to close shift dialog')
    }

    // Initialize store with factory - it will load the shift data
    shiftStore = await createShiftStore(businessId, shiftData.id.toString())

    // Use storeToRefs for reactivity with destructuring
    const { shift: storeShift, closingCashAmount: storeClosingCashAmount, permissions: storePermissions } = storeToRefs(shiftStore)

    // Watch store changes and update local refs
    watchEffect(() => {
      shift.value = storeShift.value
      closingCashAmount.value = storeClosingCashAmount.value
      permissions.value = storePermissions.value
    })
  }
  catch (error) {
    const errorMessage = error instanceof Error ? error.message : t('Failed to initialize close shift form')
    initError.value = errorMessage
    useErrorToast(errorMessage)
  }
  finally {
    loading.value = false
  }
})

// Which cash-figure warning (if any) to show beside computedExpectedCashAmount
// below. Reads the store the same way that figure does (`shiftStore?.foo`) —
// see resolveCashWarning in utils/shiftCash.ts for why the three states
// resolve in this order.
const cashWarning = computed(() => resolveCashWarning({
  methodsLoaded: shiftStore?.computedCashMethodsLoaded ?? false,
  methodMissing: shiftStore?.computedCashMethodMissing ?? false,
  unclassifiedCount: shiftStore?.computedCashUnclassified ?? 0,
}))

const canViewFinancials = computed(() => permissions.value?.can_view_shift_financials === true)

const money = (n: number | string | null | undefined) => `$${priceFormatter(Math.abs(Number(n ?? 0)))}`

/** One grey line under the title; parts with no value drop out. */
const headerMeta = computed(() => {
  const parts: string[] = []
  const who = (shift.value as any)?.user_profile?.name
  if (who) parts.push(String(who))
  if (shift.value?.opening_cash_amount) {
    parts.push(t('opening {amount}', { amount: money(shift.value.opening_cash_amount) }))
  }
  return parts.join(' · ')
})

// Three money sources, same as the admin corte: orders, check tenders and
// booking payments (their orders have shift_id null, so only the ledger
// knows which shift the money landed on).
const sales = computed(() => salesByMethod(
  [
    ...(shiftStore?.paymentBreakdown ?? []),
    ...(shiftStore?.checksPaymentBreakdown ?? []),
    ...(shiftStore?.bookingsPaymentBreakdown ?? []),
  ],
  t('Unknown'),
))

/**
 * The four drawer rows, built as data so the template cannot show one and omit
 * another. Every value is the one `computedExpectedCashAmount` consumes — see
 * the note on that computed for why `totalExpenses` must not appear here.
 */
const cashRows = computed(() => [
  { label: t('Opening cash'), value: money(shift.value?.opening_cash_amount), tone: '' },
  { label: t('Cash sales'), value: `+${money(shiftStore?.computedCashSales)}`, tone: 'text-emerald-700' },
  { label: t('Cash deposits'), value: `+${money(shiftStore?.cashDeposits)}`, tone: 'text-emerald-700' },
  { label: t('Cash expenses'), value: `-${money(shiftStore?.cashExpenses)}`, tone: 'text-red-700' },
])

/**
 * The live shortfall/surplus banner — the one thing this screen could not tell
 * a cashier before, and the reason the sign matters: a flipped subtraction
 * turns "you are $7 short" into "you have $7 extra", which reads as plausible
 * and sends the owner after the wrong person.
 *
 * Deliberately NOT `shiftStore.computedDiffCashAmount`: that reads the store's
 * own `closingCashAmount`, and this dialog's input is bound to the local ref
 * above, which never writes back to it. The banner reported "the drawer
 * matches" for a $200 count against $100 expected until this was found by
 * hand. Both halves are pinned in tests/utils/shiftCash.test.ts.
 */
const countedDiff = computed(() => typedCashDifference(
  closingCashAmount.value,
  Number(shiftStore?.computedExpectedCashAmount ?? 0),
))

const countState = computed(() => resolveCountState(countedDiff.value))

const countBanner = computed(() => {
  const diff = countedDiff.value ?? 0
  switch (countState.value) {
    case 'balanced':
      return { classes: 'border-emerald-200 bg-emerald-50 text-emerald-800', icon: 'pi-check-circle', text: t('The drawer matches what was expected'), amount: money(0) }
    case 'short':
      return { classes: 'border-amber-200 bg-amber-50 text-amber-800', icon: 'pi-exclamation-triangle', text: t('Short against expected'), amount: `-${money(diff)}` }
    case 'over':
      return { classes: 'border-sky-200 bg-sky-50 text-sky-800', icon: 'pi-arrow-up-right', text: t('Over against expected'), amount: `+${money(diff)}` }
    default:
      return { classes: 'border-gray-200 bg-gray-50 text-gray-500', icon: 'pi-info-circle', text: t('Enter the counted cash to see the difference'), amount: '—' }
  }
})

async function submitWrapper(formData: ClosingShiftForm) {
  if (!formData || !shiftData?.id || !shiftStore) {
    useErrorToast('Invalid form data')
    return
  }

  try {
    await shiftStore.closeShift(formData)
    useSuccessToast('Shift closed successfully')
    dialogRef?.value?.close()
    navigateTo('/store/pos')
  }
  catch (error) {
    const errorMessage = error instanceof Error ? error.message : t('Failed to close shift')
    useErrorToast(errorMessage)
  }
}
</script>

<template>
  <!-- Loading State -->
  
<FormKitLazyProvider config-file="true">
<div
    v-if="loading"
    class="flex justify-center items-center p-8"
  >
    <div class="text-center">
      <ProgressSpinner size="50px" />
      <p class="mt-4 text-gray-600">
        {{ t('Loading shift data') }}...
      </p>
    </div>
  </div>

  <!-- Error State -->
  <div
    v-else-if="initError"
    class="flex justify-center items-center p-8"
  >
    <div class="text-center">
      <i class="pi pi-exclamation-triangle text-red-500 text-4xl mb-4" />
      <p class="text-red-600">
        {{ initError }}
      </p>
    </div>
  </div>

  <!-- Form Content -->
  <div
    v-else
    class="flex w-full flex-col"
  >
    <!-- The title lives in PrimeVue's header; this is only the context line
         that goes with it. -->
    <div class="flex flex-col gap-4 px-6 pt-5 pb-1">
      <p
        v-if="headerMeta"
        class="-mb-1 text-sm text-gray-500"
      >
        {{ t('Shift') }} #{{ shiftStore?.shiftId }} · {{ headerMeta }}
      </p>
      <!-- Sales by method. Hidden from staff without financial permission for
           the same reason the drawer card below is. -->
      <div
        v-if="canViewFinancials && sales.methods.length"
        class="flex flex-col gap-2 rounded-xl border border-solid border-gray-200 bg-white p-4"
      >
        <div class="flex items-baseline gap-3">
          <span class="text-xs font-bold uppercase tracking-wider text-gray-400">{{ t('Shift sales') }}</span>
        </div>
        <div
          v-for="m in sales.methods"
          :key="m.label"
          class="flex items-baseline gap-3 text-sm text-gray-600"
        >
          <span>{{ m.label }}</span>
          <span class="ml-auto font-mono font-medium text-gray-900">{{ money(m.amount) }}</span>
          <span class="w-10 text-right text-xs text-gray-400">{{ m.pct }} %</span>
        </div>
        <div class="mt-1 flex items-baseline gap-3 border-0 border-t border-solid border-gray-100 pt-3">
          <span class="font-bold">{{ t('Total collected') }}</span>
          <span class="ml-auto font-mono text-xl font-semibold tracking-tight text-emerald-700">{{ money(sales.total) }}</span>
        </div>
        <!-- Only when some method carries a processor commission. -->
        <template v-if="shiftStore?.commissionTotal">
          <div class="flex items-baseline gap-3 text-sm text-gray-600">
            <span>{{ t('Total commissions') }}</span>
            <span class="ml-auto font-mono font-medium text-red-600">-{{ money(shiftStore.commissionTotal) }}</span>
          </div>
          <div class="flex items-baseline gap-3 text-sm">
            <span class="font-bold">{{ t('Net after commission') }}</span>
            <span class="ml-auto font-mono font-semibold text-emerald-700">{{ money(sales.total - shiftStore.commissionTotal) }}</span>
          </div>
        </template>
      </div>

      <!-- The drawer. These rows and the figure beneath them come from the same
           values, so a cashier can check the number they are committing. -->
      <div
        v-if="canViewFinancials"
        class="flex flex-col gap-2 rounded-xl border border-solid border-gray-200 bg-white p-4"
      >
        <span class="text-xs font-bold uppercase tracking-wider text-gray-400">{{ t('Cash in drawer') }}</span>
        <div
          v-for="row in cashRows"
          :key="row.label"
          class="flex items-baseline gap-3 text-sm text-gray-600"
        >
          <span>{{ row.label }}</span>
          <span
            class="ml-auto font-mono font-medium"
            :class="row.tone || 'text-gray-900'"
          >{{ row.value }}</span>
        </div>
        <div class="mt-1 flex items-baseline gap-3 border-0 border-t border-solid border-gray-100 pt-3">
          <span class="font-bold">{{ t('Expected in drawer') }}</span>
          <span
            data-test="expected-cash"
            class="ml-auto font-mono text-xl font-semibold tracking-tight"
          >{{ money(shiftStore?.computedExpectedCashAmount) }}</span>
        </div>
        <!-- Which message (if any) is decided by resolveCashWarning and pinned
             in components/Shift/__tests__/CashWarning.test.ts — this component
             only passes the already-resolved value through. -->
        <ShiftCashWarning
          :warning="cashWarning"
          :unclassified-count="shiftStore?.computedCashUnclassified ?? 0"
        />
      </div>
    </div>

    <!-- The form carries the padding; the footer steps back out of it with
         `-mx-6`, which lands flush now that the dialog content wrapper has none
         of its own. -->
    <FormKit
      id="order-details"
      form-class="px-6"
      type="form"
      :actions="false"
      @submit="submitWrapper"
    >
      <FormKit
        type="hidden"
        name="id"
        :value="String(shiftStore?.shiftId)"
      />
      <FormKit
        v-model="closingCashAmount"
        type="text"
        :label="t('Counted cash at close')"
        placeholder="0.00"
        name="closing_cash_amount"
        validation="required|number|decimal:2"
        validation-visibility="live"
      />
      <!-- Only meaningful to someone who can see what was expected; without
           that figure the banner would announce a difference against a number
           the reader is not allowed to know. -->
      <div
        v-if="canViewFinancials"
        class="mb-4 flex items-center gap-3 rounded-lg border border-solid px-3 py-2.5 text-sm"
        :class="countBanner.classes"
        data-test="count-banner"
      >
        <i
          class="pi text-sm"
          :class="countBanner.icon"
        />
        <span>{{ countBanner.text }}</span>
        <span
          class="ml-auto font-mono font-semibold"
          data-test="count-difference"
        >{{ countBanner.amount }}</span>
      </div>
      <FormKit
        type="textarea"
        cols="20"
        :label="`${t('Closing notes')} (${t('optional')})`"
        :placeholder="t('e.g. difference from change lent to the bar')"
        name="closing_notes"
      />
      <div class="-mx-6 flex items-center gap-3 border-0 border-t border-solid border-gray-100 px-6 py-4">
        <span class="text-sm text-gray-500">{{ t('No more sales can be recorded in this shift.') }}</span>
        <div class="ml-auto">
          <FormKit
            type="submit"
            outer-class="!mb-0"
            :label="t('Close shift')"
          />
        </div>
      </div>
    </FormKit>
  </div>
</FormKitLazyProvider>

</template>
